import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, setStoredToken, clearStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await apiClient.post("/auth/register", payload);
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (err) {
      // Non-fatal: clear local session regardless so the user is logged out.
      console.warn("Logout request failed; clearing local session anyway.", err);
    }
    clearStoredToken();
    setUser(null);
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await apiClient.post("/auth/google/session", { session_id: sessionId });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, exchangeGoogleSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
